/**
 * DEACTIVATION CARD COMPONENT
 *
 * Displays a location deactivation request in list views
 */

import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { StatusBadge } from '@/components/status-badge';
import { MapPin, Building2, XCircle } from 'lucide-react';
import type { LocationDeactivationQueue } from '@/types/database';

interface DeactivationCardProps {
  deactivation: LocationDeactivationQueue & {
    organization?: { name: string; logo_url?: string | null };
  };
  href: string;
  showDetails?: boolean;
  className?: string;
}

export function DeactivationCard({
  deactivation,
  href,
  showDetails = true,
  className,
}: DeactivationCardProps) {
  const getStatusBadgeStatus = (deactivationStatus: string) => {
    switch (deactivationStatus) {
      case 'PENDING_APPROVAL':
        return 'PENDING_REVIEW';
      case 'APPROVED':
        return 'APPROVED';
      case 'DISAPPROVED':
        return 'REJECTED';
      case 'AWAITING_IT_DECONFIG':
        return 'IT_IN_PROGRESS';
      case 'AWAITING_DATA_DECONFIG':
        return 'DATA_CONFIGURATION';
      case 'AWAITING_DEACTIVATION':
        return 'FINAL_REVIEW';
      case 'DEACTIVATED':
        return 'DEACTIVATED'; // Correct label for completed deactivation
      default:
        return 'PENDING_REVIEW';
    }
  };

  return (
    <Link to={href} className='block'>
      <Card className={`transition-all duration-200 hover:shadow-lg hover:border-primary/50 border-red-200 dark:border-red-800 ${className || ''}`}>
        <CardContent className='p-5'>
          {/* Header */}
          <div className='flex items-start gap-3 mb-4'>
            {/* Location Logo */}
            {deactivation.location_logo ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                    <img
                      src={deactivation.location_logo}
                      alt={`${deactivation.location_name} logo`}
                      className='w-full h-full object-cover'
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">Location Logo</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='w-16 h-16 rounded-lg bg-red-100 dark:bg-red-900/20 flex items-center justify-center shrink-0'>
                    <MapPin className='w-8 h-8 text-red-600 dark:text-red-400' />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">Location Logo</TooltipContent>
              </Tooltip>
            )}

            {/* Location Info */}
            <div className='flex-1 min-w-0'>
              <div className='flex items-start justify-between gap-2'>
                <div className='flex-1 min-w-0'>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100 truncate'>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{deactivation.location_name}</span>
                      </TooltipTrigger>
                      <TooltipContent side="top">Location</TooltipContent>
                    </Tooltip>
                  </h3>
                  {deactivation.organization && (
                    <div className='flex items-center gap-2 mt-1'>
                      <Building2 className='w-3 h-3 text-gray-400' />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className='text-sm text-gray-600 dark:text-gray-400 truncate'>
                            {deactivation.organization.name}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">Organization</TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
                
                {/* Deactivation Icon */}
                <div className='shrink-0'>
                  <XCircle className='w-5 h-5 text-red-600 dark:text-red-400' />
                </div>
              </div>

              {deactivation.location_description && showDetails && (
                <p className='text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2'>
                  {deactivation.location_description}
                </p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className='mb-4 pb-4 border-b border-gray-200 dark:border-gray-800'></div>

          {/* Status and Metadata */}
          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <span className='text-xs font-medium text-gray-600 dark:text-gray-400'>
                Status
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <StatusBadge status={getStatusBadgeStatus(deactivation.status) as any} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Status</TooltipContent>
              </Tooltip>
            </div>

            {showDetails && (
              <>
                <div className='flex items-center justify-between text-xs'>
                  <span className='text-gray-600 dark:text-gray-400'>
                    Requested
                  </span>
                  <span className='text-gray-900 dark:text-gray-100'>
                    {new Date(deactivation.requested_at || deactivation.created_at).toLocaleDateString()}
                  </span>
                </div>

                {deactivation.approved_at && (
                  <div className='flex items-center justify-between text-xs'>
                    <span className='text-gray-600 dark:text-gray-400'>
                      Approved
                    </span>
                    <span className='text-gray-900 dark:text-gray-100'>
                      {new Date(deactivation.approved_at).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {deactivation.disapproved_at && (
                  <div className='flex flex-col gap-1'>
                    <div className='flex items-center justify-between text-xs'>
                      <span className='text-gray-600 dark:text-gray-400'>
                        Disapproved
                      </span>
                      <span className='text-gray-900 dark:text-gray-100'>
                        {new Date(deactivation.disapproved_at).toLocaleDateString()}
                      </span>
                    </div>
                    {deactivation.disapproval_reason && (
                      <p className='text-xs text-red-600 dark:text-red-400 italic'>
                        {deactivation.disapproval_reason}
                      </p>
                    )}
                  </div>
                )}

                {deactivation.deactivated_at && (
                  <div className='flex items-center justify-between text-xs'>
                    <span className='text-gray-600 dark:text-gray-400'>
                      Deactivated
                    </span>
                    <span className='text-gray-900 dark:text-gray-100'>
                      {new Date(deactivation.deactivated_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
